/**
 * Forge Memory · Overview page
 *
 * Comprehensive memory browser: proposals inbox (highlighted),
 * 5 collection tabs (conversations, project, preferences,
 * experience, ideas), search bar, backup/restore actions.
 *
 * Design language: matches the Universe (dark cosmic, glass HUD).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  memoryApi,
  useMemoryStats,
  usePendingProposals,
} from "./memoryApi";
import { ForgeNavBar } from "../universe/Universe";
import "./memory.css";

const TABS = [
  { id: "ideas",         label: "Idee",        icon: "◈" },
  { id: "conversations", label: "Chat",        icon: "◨" },
  { id: "project",       label: "Progetto",    icon: "▤" },
  { id: "preferences",   label: "Preferenze",  icon: "◉" },
  { id: "experience",    label: "Esperienze",  icon: "▸" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16);
  }
}

// ══════════════════════════════════════════════════════════
// Proposal Inbox (pending items awaiting user approval)
// ══════════════════════════════════════════════════════════
function ProposalInbox({ proposals, onResolved }) {
  if (!proposals || proposals.length === 0) return null;

  const handle = async (action, id) => {
    try {
      if (action === "confirm") await memoryApi.confirm(id, "user");
      else await memoryApi.reject(id, "user");
      onResolved?.();
    } catch (e) {
      alert("Errore: " + e.message);
    }
  };
  const handleRule = async (target, category, rule) => {
    if (!category) return;
    try {
      await memoryApi.setRule(target, category, rule, "user");
      onResolved?.();
    } catch (e) {
      alert("Errore regola: " + e.message);
    }
  };

  return (
    <section className="mem-inbox" data-testid="memory-proposal-inbox">
      <div className="mem-inbox-head">
        <span className="mem-inbox-badge">
          <span className="mem-pulse" />
          {proposals.length} propost{proposals.length > 1 ? "e" : "a"} da rivedere
        </span>
      </div>
      <div className="mem-inbox-list">
        {proposals.map((p) => (
          <article key={p.id} className="mem-proposal" data-testid={`memory-proposal-${p.id}`}>
            <header className="mem-proposal-head">
              <span className="mem-proposal-target">{p.target_collection}</span>
              <span className="mem-proposal-conf">
                {(p.confidence * 100).toFixed(0)}% conf.
              </span>
            </header>
            <h4 className="mem-proposal-title">
              {p.payload?.title || p.payload?.key || "(senza titolo)"}
            </h4>
            {p.payload?.body && (
              <p className="mem-proposal-body">{p.payload.body}</p>
            )}
            {p.reasoning && (
              <p className="mem-proposal-reasoning">
                <em>Motivazione:</em> {p.reasoning}
              </p>
            )}
            <div className="mem-proposal-actions">
              <button
                className="mem-btn mem-btn-primary"
                onClick={() => handle("confirm", p.id)}
                data-testid={`memory-confirm-${p.id}`}
              >
                Salva
              </button>
              <button
                className="mem-btn"
                onClick={() => handle("reject", p.id)}
                data-testid={`memory-reject-${p.id}`}
              >
                Ignora
              </button>
              {p.payload?.category && (
                <>
                  <button
                    className="mem-btn mem-btn-ghost"
                    onClick={() =>
                      handleRule(p.target_collection, p.payload.category, "always")
                    }
                    title={`Sempre salva ${p.target_collection}/${p.payload.category}`}
                  >
                    Sempre
                  </button>
                  <button
                    className="mem-btn mem-btn-ghost"
                    onClick={() =>
                      handleRule(p.target_collection, p.payload.category, "never")
                    }
                    title={`Mai salvare ${p.target_collection}/${p.payload.category}`}
                  >
                    Mai
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// Collection browser
// ══════════════════════════════════════════════════════════
function CollectionList({ collection, refreshKey }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    memoryApi
      .list(collection, 50)
      .then((d) => !cancel && setDocs(d.docs || []))
      .catch((e) => !cancel && setError(e.message))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [collection, refreshKey]);

  if (loading) return <div className="mem-loading">Caricamento…</div>;
  if (error) return <div className="mem-error">{error}</div>;
  if (docs.length === 0) return <div className="mem-empty">Nessun dato in questa collezione ancora.</div>;

  return (
    <ul className="mem-list" data-testid={`memory-list-${collection}`}>
      {docs.map((d) => (
        <li key={d.id} className="mem-item">
          <div className="mem-item-main">
            <div className="mem-item-title">
              {d.title || d.key || d.problem || d.id}
              {d.status && <span className="mem-item-status">{d.status}</span>}
              {d.kind && <span className="mem-item-kind">{d.kind}</span>}
              {d.category && collection === "preferences" && (
                <span className="mem-item-cat">{d.category}</span>
              )}
            </div>
            {(d.summary || d.body || d.lesson_learned) && (
              <div className="mem-item-body">
                {(d.summary || d.body || d.lesson_learned).slice(0, 220)}
              </div>
            )}
            <div className="mem-item-meta">
              <span>{fmtDate(d.updated_at || d.created_at)}</span>
              {d.tags && d.tags.length > 0 && (
                <span className="mem-item-tags">
                  {d.tags.slice(0, 5).map((t) => (
                    <span key={t} className="mem-tag">#{t}</span>
                  ))}
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ══════════════════════════════════════════════════════════
// Search bar
// ══════════════════════════════════════════════════════════
function SearchPanel({ onResults }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!q.trim()) { onResults(null); return; }
    setBusy(true);
    try {
      const r = await memoryApi.search({ query: q.trim(), limit_per_collection: 30 });
      onResults(r);
    } catch (e) {
      onResults({ error: e.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mem-search">
      <input
        type="text"
        placeholder="Cerca in tutta la memoria (titolo, tag, contenuto)…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && run()}
        data-testid="memory-search-input"
      />
      <button
        onClick={run}
        disabled={busy}
        className="mem-btn mem-btn-primary"
        data-testid="memory-search-btn"
      >
        {busy ? "Cerco…" : "Cerca"}
      </button>
      {q && (
        <button
          className="mem-btn mem-btn-ghost"
          onClick={() => { setQ(""); onResults(null); }}
        >
          Pulisci
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Actions bar (backup/restore/consolidate)
// ══════════════════════════════════════════════════════════
function ActionsBar({ stats, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const runBackup = async () => {
    setBusy("backup");
    try {
      const r = await memoryApi.backup("user");
      alert(`Backup creato\n${r.path}\n${r.size_bytes} bytes\nsha256: ${r.sha256.slice(0,16)}…`);
      onRefresh?.();
    } catch (e) {
      alert("Errore backup: " + e.message);
    } finally {
      setBusy(null);
    }
  };
  const runConsolidate = async () => {
    setBusy("consolidate");
    try {
      const r = await memoryApi.consolidate();
      alert("Consolidamento completato\n" + JSON.stringify(r, null, 2).slice(0, 400));
      onRefresh?.();
    } catch (e) {
      alert("Errore: " + e.message);
    } finally { setBusy(null); }
  };
  const runIntegrity = async () => {
    setBusy("integrity");
    try {
      const r = await memoryApi.integrity();
      alert(r.ok ? `Integrità OK (${r.issue_count} problemi)` :
                    `Problemi: ${r.issue_count}\n${(r.issues || []).slice(0,3).join("\n")}`);
    } catch (e) {
      alert("Errore: " + e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="mem-actions">
      <button
        className="mem-btn mem-btn-outline"
        onClick={runBackup}
        disabled={busy === "backup"}
        data-testid="memory-backup-btn"
      >
        {busy === "backup" ? "Backup…" : "Backup ora"}
      </button>
      <button
        className="mem-btn mem-btn-outline"
        onClick={runConsolidate}
        disabled={busy === "consolidate"}
        data-testid="memory-consolidate-btn"
      >
        Consolida
      </button>
      <button
        className="mem-btn mem-btn-outline"
        onClick={runIntegrity}
        disabled={busy === "integrity"}
        data-testid="memory-integrity-btn"
      >
        Verifica integrità
      </button>
      {stats?.last_backup_at && (
        <span className="mem-actions-meta">
          Ultimo backup: {fmtDate(stats.last_backup_at)}
        </span>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Page root
// ══════════════════════════════════════════════════════════
export default function Memory() {
  const [tab, setTab] = useState("ideas");
  const [searchResults, setSearchResults] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { stats, refresh: refreshStats } = useMemoryStats(60000);
  const { proposals, refresh: refreshProposals } = usePendingProposals(15000);

  const forceRefresh = () => {
    setRefreshKey((k) => k + 1);
    refreshStats();
    refreshProposals();
  };

  return (
    <div className="mem-root" data-testid="memory-page">
      <ForgeNavBar current="/memory" />
      <header className="mem-header">
        <div>
          <div className="mem-brand">FORGE · MEMORY ENGINE</div>
          <div className="mem-sub">
            Memoria persistente · schema v{stats?.schema_version ?? "…"} ·
            {stats && (
              <> {stats.conversations} chat · {stats.ideas} idee · {stats.project} progetto ·
                 {stats.preferences} preferenze · {stats.experience} esperienze</>
            )}
          </div>
        </div>
      </header>

      <ProposalInbox proposals={proposals} onResolved={forceRefresh} />

      <ActionsBar stats={stats} onRefresh={forceRefresh} />

      <SearchPanel onResults={setSearchResults} />

      {searchResults ? (
        <SearchResultsView results={searchResults} />
      ) : (
        <>
          <nav className="mem-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={"mem-tab " + (tab === t.id ? "active" : "")}
                onClick={() => setTab(t.id)}
                data-testid={`memory-tab-${t.id}`}
              >
                <span className="mem-tab-icon">{t.icon}</span>
                {t.label}
                {stats && <span className="mem-tab-count">{stats[t.id] ?? 0}</span>}
              </button>
            ))}
          </nav>
          <CollectionList collection={tab} refreshKey={refreshKey} />
        </>
      )}
    </div>
  );
}

function SearchResultsView({ results }) {
  if (results.error) return <div className="mem-error">{results.error}</div>;
  const entries = Object.entries(results.results || {}).filter(
    ([, docs]) => Array.isArray(docs) && docs.length > 0
  );
  if (entries.length === 0) {
    return <div className="mem-empty">Nessun risultato per &quot;{results.query}&quot;.</div>;
  }
  return (
    <div className="mem-search-results" data-testid="memory-search-results">
      <div className="mem-search-summary">
        {results.total} risultati per &quot;{results.query}&quot;
      </div>
      {entries.map(([coll, docs]) => (
        <section key={coll} className="mem-search-section">
          <h3>{coll} <em>{docs.length}</em></h3>
          <ul className="mem-list">
            {docs.map((d) => (
              <li key={d.id} className="mem-item">
                <div className="mem-item-title">
                  {d.title || d.key || d.problem || d.id}
                </div>
                {(d.summary || d.body || d.lesson_learned) && (
                  <div className="mem-item-body">
                    {(d.summary || d.body || d.lesson_learned).slice(0, 240)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
